import styles from './add-new-user-dialog.module.scss';
import { useContext, useState } from 'react';
import { useGetRequestNewHook, useMakeRequestHook } from '../../requestUtils';
import { ValueFormatterParams } from 'ag-grid-community';
import { UseSignalRHook } from '../../signalr-context/signalr-context';
import { Checkbox, CreateHeaders, DatePicker, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, HeaderSizeEnum, OrderTypesEnum, PrimaryButton, SnackBarContext, textHeader, Typography } from '@frontend-workspace/react-shared-lib';
import WebkuaTable from '../../webkua-table/webkua-table';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface AddNewUserDialogProps {
  open: boolean,
  onClose: () => void,
  companyNumber: string,
  contractId: string,
  isGroupDeal: boolean,
  maxUsersOnContract: number,
  currentUsersPersonIds: string[]
}

export function AddNewUserDialog(props: AddNewUserDialogProps) {
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const makeRequest = useMakeRequestHook();
  const { data: employees } = useGetRequestNewHook(`companies/${props.companyNumber}/userAccounts/forGroup/true/onlyActive/true`, []);
  const [dateValue, setDateValue] = useState<Date | null>(new Date());
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>(props.currentUsersPersonIds);
  const { t } = useTranslation();

  const selectUserRenderer = (params: ValueFormatterParams) => {
    const isSelected = selectedPersonIds.includes(params.value);
    const handleChange = () => {
      isSelected ?
        setSelectedPersonIds(selectedPersonIds.filter(id => id !== params.value)) :
        setSelectedPersonIds(selectedPersonIds.concat(params.value));
    };
    return <Checkbox
      checked={isSelected}
      onChange={handleChange}
    />
  };

  const onSignalRReceived = () => {
    setSnackbarMessage({ type: "success", content: t('addNewUser.contractUpdated') });
    props.onClose();
  };

  UseSignalRHook(onSignalRReceived, [OrderTypesEnum.ChangeLicenseAccess]);

  const handleChangeUsers = () => {
    if (selectedPersonIds.length > props.maxUsersOnContract) {
      setSnackbarMessage({ type: "error", content: t('addNewUser.cannotHaveMoreUsersThanThereAreSeats') });
    } else {
      setSnackbarMessage({ type: "info", content: t('addNewUser.updatesUsersOnContract') });
      const body = {
        emails: selectedPersonIds,
        companyNumber: props.companyNumber,
        contractId: props.contractId,
        activationDate: dateValue
      };
      makeRequest(`contracts/action/changeSupportUsersOnContract`, 'PUT', body).then();
    }
  };

  const nameRenderer = (params: ValueFormatterParams) => `${params.data.firstName} ${params.data.lastName}`;

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="xl"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t('standard.changeUsers')}
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body6"
          component="div"
          className="mb-4 me-4">
          {`${t('addNewUser.numberOfUsersOnContract')}: ${selectedPersonIds.length}/${props.maxUsersOnContract}`}
        </Typography>
        <div className='mb-3 mb-md-0'>
          <DatePicker
            inputFormat="dd.MM.yyyy"
            value={dateValue}
            label={t('standard.executionDate')}
            onChange={setDateValue}
          />
        </div>
        <WebkuaTable
          headers={CreateHeaders([
            textHeader("email", "", HeaderSizeEnum.EXTRA_SMALL, { renderer: selectUserRenderer }),
            textHeader("firstName", t('standard.employee'), HeaderSizeEnum.LARGE, { renderer: nameRenderer }),
            textHeader("email", t('standard.email'), HeaderSizeEnum.LARGE)
          ])}
          data={employees}
        />
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant="contained"
          size='large'
          onClick={handleChangeUsers}
          className='minButtonWidth'>
          OK
        </PrimaryButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t('standard.cancel')}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default AddNewUserDialog;